import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, Grid, IconButton, ListItemText, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { Add, Delete, Edit, RestartAlt, Save } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import api from '../../services/api';
import { getApiErrorMessage } from '../../services/userService';
import { formatMoney } from '../../utils/currency';
import {
  ACCOUNT_LABELS, ACCOUNT_TYPES, AccountType, ReferralConfig, ReferralRule, SERVICE_LABELS, TRIGGER_LABELS,
  ReferralTrigger, fetchRewardsConfig, useRewardsTenant,
} from './rewardsShared';

function num(value: string, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

export default function ReferralSettingsPage() {
  const tenant = useRewardsTenant();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ReferralConfig | null>(null);
  const [editing, setEditing] = useState<ReferralRule | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['rewards-config', tenant.scopeKey],
    queryFn: () => fetchRewardsConfig(tenant.headers),
    enabled: tenant.ready,
  });

  useEffect(() => {
    if (data) setDraft(structuredClone(data.referral));
  }, [data]);

  const save = useMutation({
    mutationFn: (config: ReferralConfig) => api.put('/admin/rewards/referral-config', config, { headers: tenant.headers }),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Referral settings saved.' });
      queryClient.invalidateQueries({ queryKey: ['rewards-config'] });
    },
    onError: (err) => setMessage({ type: 'error', text: getApiErrorMessage(err, 'Could not save referral settings') }),
  });

  const currency = data?.meta.currency;
  const defaults = data?.meta.referralDefaults;
  const services = data?.meta.services || Object.keys(SERVICE_LABELS);
  const fmt = (rule: ReferralRule, value: number) => (
    rule.rewardType === 'points' ? `${Math.floor(value)} pts` : formatMoney(value, { symbol: currency?.symbol, currency: currency?.code })
  );

  const ruleFor = (referrer: AccountType, referee: AccountType) =>
    draft?.rules.find((r) => r.referrerType === referrer && r.refereeType === referee);

  const updateRule = (next: ReferralRule) => {
    setDraft((d) => d && ({
      ...d,
      rules: d.rules.map((r) => (r.referrerType === next.referrerType && r.refereeType === next.refereeType ? next : r)),
    }));
  };

  return (
    <PageShell>
      <PageHeader
        eyebrow="Referrals & Points"
        title="Referral settings"
        subtitle="Configure who can refer whom, how much each side earns, and when the reward is released."
        breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Referral settings' }]}
        actions={(
          <Stack direction="row" spacing={1} alignItems="center">
            {tenant.picker}
            <Button
              variant="contained"
              startIcon={<Save />}
              disabled={!draft || save.isPending}
              onClick={() => draft && save.mutate(draft)}
            >
              Save
            </Button>
          </Stack>
        )}
      />
      {tenant.notReady}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{getApiErrorMessage(error, 'Could not load settings')}</Alert>}
      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      {data && !data.referralConfigured && (
        <Alert severity="info" sx={{ mb: 2 }}>
          These are default values derived from the tenant settings. Save to make them the active program.
        </Alert>
      )}
      {isLoading && <Typography color="text.secondary">Loading…</Typography>}

      {draft && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Program</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={<Switch checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />}
                    label="Referral program enabled"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={<Switch checked={draft.preventSameDevice} onChange={(e) => setDraft({ ...draft, preventSameDevice: e.target.checked })} />}
                    label="Block same-device referrals"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={<Switch checked={draft.allowLateApply} onChange={(e) => setDraft({ ...draft, allowLateApply: e.target.checked })} />}
                    label="Allow applying a code after signup"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Late apply window (days)"
                    disabled={!draft.allowLateApply}
                    value={draft.lateApplyDays}
                    onChange={(e) => setDraft({ ...draft, lateApplyDays: num(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Share message"
                    helperText="Use {code} for the referral code and {app} for the app name. Left empty, the default message is used."
                    value={draft.shareMessage}
                    onChange={(e) => setDraft({ ...draft, shareMessage: e.target.value })}
                  />
                  {defaults && (
                    <Button
                      size="small"
                      startIcon={<RestartAlt />}
                      disabled={draft.shareMessage.trim() === defaults.shareMessage}
                      onClick={() => setDraft({ ...draft, shareMessage: defaults.shareMessage })}
                    >
                      Reset to default
                    </Button>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Extra terms (one per line)"
                    helperText="Reward terms are generated automatically; add anything else here. Left empty, the default terms are used."
                    value={draft.terms.join('\n')}
                    onChange={(e) => setDraft({ ...draft, terms: e.target.value.split('\n') })}
                  />
                  {defaults && (
                    <Button
                      size="small"
                      startIcon={<RestartAlt />}
                      disabled={draft.terms.map((t) => t.trim()).filter(Boolean).join('\n') === defaults.terms.join('\n')}
                      onClick={() => setDraft({ ...draft, terms: [...defaults.terms] })}
                    >
                      Reset to default
                    </Button>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Reward matrix</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Rows are who shares the code (referrer), columns are who joins with it (referee).
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '160px repeat(3, minmax(220px, 1fr))',
                gap: 1.5,
                minWidth: 860,
              }}
            >
              <Box />
              {ACCOUNT_TYPES.map((referee) => (
                <Typography key={referee} variant="overline" fontWeight={700} textAlign="center">
                  Invites {ACCOUNT_LABELS[referee]}
                </Typography>
              ))}
              {ACCOUNT_TYPES.map((referrer) => (
                <Box key={referrer} sx={{ display: 'contents' }}>
                  <Stack justifyContent="center">
                    <Typography variant="subtitle2" fontWeight={700}>{ACCOUNT_LABELS[referrer]}</Typography>
                    <Typography variant="caption" color="text.secondary">refers…</Typography>
                  </Stack>
                  {ACCOUNT_TYPES.map((referee) => {
                    const rule = ruleFor(referrer, referee);
                    if (!rule) return <Box key={referee} />;
                    return (
                      <Card
                        key={referee}
                        variant="outlined"
                        sx={{ opacity: rule.enabled ? 1 : 0.6, borderColor: rule.enabled ? 'success.light' : 'divider' }}
                      >
                        <CardContent sx={{ pb: '12px !important' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Switch
                              size="small"
                              checked={rule.enabled}
                              onChange={(e) => updateRule({ ...rule, enabled: e.target.checked })}
                            />
                            <IconButton size="small" onClick={() => setEditing(structuredClone(rule))} aria-label="Edit rule">
                              <Edit fontSize="small" />
                            </IconButton>
                          </Stack>
                          <Typography variant="body2">Referrer: <b>{fmt(rule, rule.referrerReward)}</b></Typography>
                          <Typography variant="body2">Referee: <b>{fmt(rule, rule.refereeReward)}</b></Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            <Chip size="small" label={rule.rewardType === 'points' ? 'Points' : 'Wallet'} />
                            <Chip
                              size="small"
                              label={rule.trigger === 'n_completions' ? `${rule.requiredCompletions} completions` : TRIGGER_LABELS[rule.trigger]}
                            />
                            {rule.requireApproval && referee !== 'user' && <Chip size="small" label="Needs approval" />}
                            {rule.serviceAmounts.length > 0 && <Chip size="small" label="Per-service" />}
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}

      {editing && (
        <RuleDialog
          rule={editing}
          services={services}
          currencySymbol={currency?.symbol || ''}
          onClose={() => setEditing(null)}
          onSave={(rule) => {
            updateRule(rule);
            setEditing(null);
          }}
        />
      )}
    </PageShell>
  );
}

function RuleDialog({
  rule: initial,
  services,
  currencySymbol,
  onClose,
  onSave,
}: {
  rule: ReferralRule;
  services: string[];
  currencySymbol: string;
  onClose: () => void;
  onSave: (rule: ReferralRule) => void;
}) {
  const [rule, setRule] = useState<ReferralRule>(initial);
  const unit = rule.rewardType === 'points' ? 'points' : currencySymbol || 'amount';
  const set = <K extends keyof ReferralRule>(key: K, value: ReferralRule[K]) => setRule((r) => ({ ...r, [key]: value }));
  const unusedServices = services.filter((s) => !rule.serviceAmounts.some((a) => a.service === s));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {ACCOUNT_LABELS[rule.referrerType]} invites {ACCOUNT_LABELS[rule.refereeType]}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControlLabel control={<Switch checked={rule.enabled} onChange={(e) => set('enabled', e.target.checked)} />} label="Enabled" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Reward paid as" value={rule.rewardType} onChange={(e) => set('rewardType', e.target.value as ReferralRule['rewardType'])}>
              <MenuItem value="wallet">Wallet money</MenuItem>
              <MenuItem value="points">Loyalty points</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            {rule.refereeType !== 'user' && (
              <FormControlLabel
                control={<Switch checked={rule.requireApproval} onChange={(e) => set('requireApproval', e.target.checked)} />}
                label="Wait for account approval"
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="number" label={`Referrer reward (${unit})`} value={rule.referrerReward} onChange={(e) => set('referrerReward', num(e.target.value))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="number" label={`Referee reward (${unit})`} value={rule.refereeReward} onChange={(e) => set('refereeReward', num(e.target.value))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Release reward" value={rule.trigger} onChange={(e) => set('trigger', e.target.value as ReferralTrigger)}>
              {(Object.keys(TRIGGER_LABELS) as ReferralTrigger[]).map((t) => (
                <MenuItem key={t} value={t}>{TRIGGER_LABELS[t]}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Required completions"
              disabled={rule.trigger !== 'n_completions'}
              value={rule.trigger === 'n_completions' ? rule.requiredCompletions : 1}
              onChange={(e) => set('requiredCompletions', Math.max(1, Math.floor(num(e.target.value, 1))))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={`Minimum trip/order amount (${currencySymbol || 'amount'})`}
              helperText="0 = any amount"
              disabled={rule.trigger === 'signup'}
              value={rule.minOrderAmount}
              onChange={(e) => set('minOrderAmount', num(e.target.value))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Qualifying services"
              helperText="Leave empty to count every service"
              disabled={rule.trigger === 'signup'}
              SelectProps={{
                multiple: true,
                renderValue: (v) => ((v as string[]).length ? (v as string[]).map((s) => SERVICE_LABELS[s] || s).join(', ') : 'All services'),
              }}
              value={rule.services}
              onChange={(e) => set('services', (typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value) as string[])}
            >
              {services.map((s) => (
                <MenuItem key={s} value={s}>
                  <Checkbox size="small" checked={rule.services.includes(s)} />
                  <ListItemText primary={SERVICE_LABELS[s] || s} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="number" label="Validity (days)" helperText="0 = never expires" value={rule.validityDays} onChange={(e) => set('validityDays', Math.floor(num(e.target.value)))} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="number" label="Max rewards / month" helperText="Per referrer, 0 = unlimited" value={rule.maxPerMonth} onChange={(e) => set('maxPerMonth', Math.floor(num(e.target.value)))} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="number" label="Max rewards lifetime" helperText="Per referrer, 0 = unlimited" value={rule.maxLifetime} onChange={(e) => set('maxLifetime', Math.floor(num(e.target.value)))} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>Per-service amounts</Typography>
            <Typography variant="caption" color="text.secondary">
              Override the amounts above when the qualifying trip/job is for a specific service.
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<Add />}
            disabled={!unusedServices.length || rule.trigger === 'signup'}
            onClick={() => set('serviceAmounts', [
              ...rule.serviceAmounts,
              { service: unusedServices[0], referrerReward: rule.referrerReward, refereeReward: rule.refereeReward },
            ])}
          >
            Add
          </Button>
        </Stack>
        {rule.serviceAmounts.map((row, idx) => (
          <Stack key={`${row.service}-${idx}`} direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <TextField
              select
              size="small"
              label="Service"
              sx={{ minWidth: 180 }}
              value={row.service}
              onChange={(e) => set('serviceAmounts', rule.serviceAmounts.map((a, i) => (i === idx ? { ...a, service: e.target.value } : a)))}
            >
              {services.filter((s) => s === row.service || unusedServices.includes(s)).map((s) => (
                <MenuItem key={s} value={s}>{SERVICE_LABELS[s] || s}</MenuItem>
              ))}
            </TextField>
            <TextField size="small" type="number" label={`Referrer (${unit})`} value={row.referrerReward}
              onChange={(e) => set('serviceAmounts', rule.serviceAmounts.map((a, i) => (i === idx ? { ...a, referrerReward: num(e.target.value) } : a)))} />
            <TextField size="small" type="number" label={`Referee (${unit})`} value={row.refereeReward}
              onChange={(e) => set('serviceAmounts', rule.serviceAmounts.map((a, i) => (i === idx ? { ...a, refereeReward: num(e.target.value) } : a)))} />
            <IconButton aria-label="Remove" onClick={() => set('serviceAmounts', rule.serviceAmounts.filter((_, i) => i !== idx))}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(rule)}>Apply</Button>
      </DialogActions>
    </Dialog>
  );
}
